/* ═══════════════════════ CLOUD — Supabase ═══════════════════════
   Supabase-klient, dagbok-synk, realtime, reaksjoner og familie-deling.
   Krever globalen `supabase` lastet via CDN i index.html.
════════════════════════════════════════════════════════════════ */

import {
  S, saveDiary, saveComments, saveReactions,
  saveActs, saveBudget, savePackChk,
} from './state.js';
import { esc, compressImage, dataUrlToBlob } from './utils.js';
import { renderPage, renderNav } from './main.js';
import { enqueue as syncEnqueue } from './syncQueue.js';

/* ── Konfigurasjon ── */
const SB_URL = "https://hlvbhawikzkadendyrfj.supabase.co";
const SB_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhsdmJoYXdpa3prYWRlbmR5cmZqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzUwNTkzNTMsImV4cCI6MjA5MDYzNTM1M30.idR5O9AAriIxV0vy7tCVWVEDFc7PJOypwXZd7oTsaiI";

export const SB_READY = SB_URL !== "SUPABASE_URL_HER";
export const MOODS = [{e:"😍",l:"Fantastisk"},{e:"😊",l:"Bra"},{e:"😐",l:"OK"},{e:"😴",l:"Trøtt"},{e:"🌧️",l:"Regnvær"}];
export const FAMILY = ["Robert","Margita","Nicholas","Alexander"];
const DIARY_BUCKET = "diary-photos";

let sb = null;
export function getSB(){
  if(!sb && SB_READY){
    sb = supabase.createClient(SB_URL, SB_KEY);
  }
  return sb;
}

/* ── Toast ── */
export function showToast(msg){
  let t=document.getElementById("global-toast");
  if(!t){t=document.createElement("div");t.id="global-toast";
    t.style.cssText="position:fixed;top:80px;left:50%;transform:translateX(-50%);background:var(--bg3);border:1px solid var(--border);color:var(--text);padding:10px 18px;border-radius:20px;font-size:13px;font-weight:600;z-index:400;pointer-events:none;transition:opacity .3s;max-width:280px;text-align:center";
    document.body.appendChild(t);}
  t.textContent=msg;t.style.opacity="1";t.style.display="block";
  clearTimeout(window._gt);window._gt=setTimeout(()=>{t.style.opacity="0";setTimeout(()=>t.style.display="none",300);},3000);
}

/* ── Dagbok-cloud ── */
export async function loadDiaryFromCloud(){
  const client=getSB();if(!client)return;
  try{
    const {data,error}=await client.from("diary_entries").select("*").order("created_at",{ascending:false});
    if(!error&&data){
      S.diary=data.map(r=>({
        id:r.id,dayIdx:r.day_idx,title:r.title,text:r.text,
        mood:r.mood,author:r.author,photos:r.photos||[],
        ts:new Date(r.created_at).getTime()
      }));
      saveDiary();
    }
  }catch(e){console.warn("Supabase diary load error",e);}
  try{
    const {data,error}=await client.from("diary_comments").select("*").order("created_at",{ascending:true});
    if(!error&&data){
      const grouped={};
      data.forEach(r=>{
        if(!grouped[r.entry_id])grouped[r.entry_id]=[];
        grouped[r.entry_id].push({id:r.id,author:r.author,text:r.text,ts:new Date(r.created_at).getTime()});
      });
      S.comments=grouped;
      saveComments();
    }
  }catch(e){console.warn("Supabase comments load error",e);}
  await loadReactionsFromCloud();
}

export async function uploadPhoto(file, entryId, idx){
  const client=getSB();if(!client)return null;
  const {dataUrl, mime, ext}=await compressImage(file,1200,0.82);
  const path=`${entryId}/${idx}_${Date.now()}.${ext}`;
  const blob=dataUrlToBlob(dataUrl); // ikke fetch(data:) — blokkeres av CSP connect-src
  const{error}=await client.storage.from(DIARY_BUCKET).upload(path,blob,{upsert:true,contentType:mime});
  if(error){console.warn("Upload error",error);return null;}
  const{data:pub}=client.storage.from(DIARY_BUCKET).getPublicUrl(path);
  return pub.publicUrl;
}

/* ── Realtime — dagbok ── */
let realtimeSub=null;
export function startRealtime(){
  const client=getSB();
  if(!client||realtimeSub)return;
  realtimeSub=client
    .channel("diary_live")
    .on("postgres_changes",{event:"INSERT",schema:"public",table:"diary_entries"},payload=>{
      const r=payload.new;
      if(S.diary.find(d=>d.id===r.id))return;
      S.diary=[{id:r.id,dayIdx:r.day_idx,title:r.title,text:r.text,
        mood:r.mood,author:r.author,photos:r.photos||[],
        ts:new Date(r.created_at).getTime()},...S.diary];
      saveDiary();
      if(S.tab==="dagbok"&&!S.diaryEdit)renderPage();
      showToast("📖 "+(r.author||"Noen")+" postet: "+(r.title||"Nytt innlegg"));
    })
    .on("postgres_changes",{event:"DELETE",schema:"public",table:"diary_entries"},payload=>{
      S.diary=S.diary.filter(d=>d.id!==payload.old.id);
      saveDiary();
      if(S.tab==="dagbok"&&!S.diaryEdit)renderPage();
    })
    .on("postgres_changes",{event:"INSERT",schema:"public",table:"diary_comments"},payload=>{
      const r=payload.new;
      if(!S.comments[r.entry_id])S.comments[r.entry_id]=[];
      if(S.comments[r.entry_id].find(c=>c.id===r.id))return;
      S.comments[r.entry_id]=[...S.comments[r.entry_id],
        {id:r.id,author:r.author,text:r.text,ts:new Date(r.created_at).getTime()}];
      saveComments();
      if(S.tab==="dagbok"&&!S.diaryEdit)renderPage();
    })
    .on("postgres_changes",{event:"INSERT",schema:"public",table:"diary_reactions"},payload=>{
      const r=payload.new;
      if(!S.reactions[r.entry_id])S.reactions[r.entry_id]={};
      if(!S.reactions[r.entry_id][r.emoji])S.reactions[r.entry_id][r.emoji]=[];
      if(!S.reactions[r.entry_id][r.emoji].includes(r.author)){
        S.reactions[r.entry_id][r.emoji]=[...S.reactions[r.entry_id][r.emoji],r.author];
        saveReactions();
        if(S.tab==="dagbok"&&!S.diaryEdit)renderPage();
      }
    })
    .on("postgres_changes",{event:"DELETE",schema:"public",table:"diary_reactions"},payload=>{
      const r=payload.old;
      if(S.reactions[r.entry_id]?.[r.emoji]){
        S.reactions[r.entry_id][r.emoji]=S.reactions[r.entry_id][r.emoji].filter(a=>a!==r.author);
        saveReactions();
        if(S.tab==="dagbok"&&!S.diaryEdit)renderPage();
      }
    })
    .subscribe();
}

/* ── Reaksjoner ── */
const REACTION_EMOJIS=["❤️","😂","👏","🔥","😍","🙏"];

export function renderReactions(entryId){
  const rxs=S.reactions[entryId]||{};
  const name=S.diaryAuthor||S.commentName||"";
  let html='<div style="display:flex;flex-wrap:wrap;gap:5px;margin:10px 0 6px">';
  REACTION_EMOJIS.forEach(function(em){
    const who=rxs[em]||[];
    const isMine=!!(name&&who.includes(name));
    const border=isMine?"var(--gold)":"var(--border2)";
    const bg=isMine?"rgba(201,145,58,.18)":"var(--bg3)";
    const cnt=who.length>0?('<span style="font-size:11px;font-weight:700;color:'+(isMine?"var(--gold)":"var(--t2)")+'">'+who.length+'</span>'):"";
    const title=who.length>0?who.join(", "):"Ingen ennå";
    html+='<button onclick="toggleReaction(\''+entryId+'\',\''+em+'\')" title="'+esc(title)+'" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;border-radius:20px;border:1px solid '+border+';background:'+bg+';cursor:pointer;font-size:14px;font-family:var(--font);transition:all .15s">'+em+cnt+'</button>';
  });
  html+='</div>';
  return html;
}

export async function toggleReaction(entryId,emoji){
  const name=S.diaryAuthor||S.commentName||"";
  if(!name){showToast("Velg navn først (under 'Du er' øverst, eller skriv navn i kommentarfeltet)");return;}
  if(!S.reactions[entryId])S.reactions[entryId]={};
  if(!S.reactions[entryId][emoji])S.reactions[entryId][emoji]=[];
  const idx=S.reactions[entryId][emoji].indexOf(name);
  const adding=idx===-1;
  if(adding) S.reactions[entryId][emoji]=[...S.reactions[entryId][emoji],name];
  else        S.reactions[entryId][emoji]=S.reactions[entryId][emoji].filter(a=>a!==name);
  saveReactions();
  renderPage();
  if(adding){
    syncEnqueue({kind:"insert",table:"diary_reactions",data:{entry_id:entryId,emoji,author:name,created_at:new Date().toISOString()}});
  } else {
    syncEnqueue({kind:"delete",table:"diary_reactions",where:{entry_id:entryId,emoji,author:name}});
  }
}

export async function loadReactionsFromCloud(){
  const client=getSB();if(!client)return;
  try{
    const{data,error}=await client.from("diary_reactions").select("*");
    if(!error&&data){
      const grouped={};
      data.forEach(r=>{
        if(!grouped[r.entry_id])grouped[r.entry_id]={};
        if(!grouped[r.entry_id][r.emoji])grouped[r.entry_id][r.emoji]=[];
        if(!grouped[r.entry_id][r.emoji].includes(r.author)) grouped[r.entry_id][r.emoji].push(r.author);
      });
      S.reactions=grouped;saveReactions();
    }
  }catch(e){console.warn("Reactions load error",e);}
}

/* ── Familie-synk ── */
export function enableFamilySync(){
  const name=document.getElementById("family-name-input")?.value?.trim();
  if(!name){showToast("Skriv inn navnet ditt");return;}
  S.familyName=name;S.syncEnabled=true;
  try{localStorage.setItem("ca26fname",name);localStorage.setItem("ca26sync","1");}catch{}
  showToast(`👨‍👩‍👧‍👦 Deling aktivert som ${name}!`);
  syncAllToCloud();
  startSharedRealtime();
  renderPage();
}

export function disableFamilySync(){
  S.syncEnabled=false;
  try{localStorage.setItem("ca26sync","0");}catch{}
  showToast("Deling deaktivert");
  renderPage();
}

export async function syncAllToCloud(){
  if(!SB_READY||!S.syncEnabled||!navigator.onLine)return;
  const client=getSB();if(!client)return;
  const now=new Date().toISOString();
  const types=[
    {type:"acts",data:S.acts},
    {type:"budget",data:S.budgetEntries},
    {type:"pack",data:S.packChk},
  ];
  for(const t of types){
    await client.from("shared_state").upsert({
      id:t.type,type:t.type,data:t.data,author:S.familyName,updated_at:now
    },{onConflict:"id"});
  }
  S.lastSync=new Date();
  updateSyncStatus();
}

export async function loadSharedState(){
  if(!SB_READY||!S.syncEnabled||!navigator.onLine)return;
  const client=getSB();if(!client)return;
  const {data}=await client.from("shared_state").select("*");
  if(!data)return;
  let changed=false;
  for(const row of data){
    if(row.type==="acts"&&row.author!==S.familyName&&Array.isArray(row.data)){
      const localIds=new Set(S.acts.map(a=>a.id));
      const newActs=row.data.filter(a=>!localIds.has(a.id));
      if(newActs.length>0){S.acts=[...S.acts,...newActs];saveActs();changed=true;}
    }
    if(row.type==="budget"&&row.author!==S.familyName&&Array.isArray(row.data)){
      const localIds=new Set(S.budgetEntries.map(e=>e.id));
      const newEntries=row.data.filter(e=>!localIds.has(e.id));
      if(newEntries.length>0){S.budgetEntries=[...S.budgetEntries,...newEntries];saveBudget();changed=true;}
    }
    if(row.type==="pack"&&row.data&&typeof row.data==="object"){
      Object.assign(S.packChk,row.data);savePackChk();changed=true;
    }
  }
  S.lastSync=new Date();
  if(changed)renderPage();
  updateSyncStatus();
}

export function updateSyncStatus(){
  const el=document.getElementById("sync-status");
  if(!el||!S.lastSync)return;
  const ago=Math.round((Date.now()-S.lastSync.getTime())/1000);
  el.textContent=ago<60?`Synket nå`:`Synket ${Math.round(ago/60)} min siden`;
}

let sharedRealtimeSub=null;
export function startSharedRealtime(){
  if(!SB_READY||!S.syncEnabled||sharedRealtimeSub)return;
  const client=getSB();if(!client)return;
  sharedRealtimeSub=client
    .channel("shared_live")
    .on("postgres_changes",{event:"*",schema:"public",table:"shared_state"},payload=>{
      const r=payload.new;
      if(!r||r.author===S.familyName)return;
      if(r.type==="acts"&&Array.isArray(r.data)){
        const localIds=new Set(S.acts.map(a=>a.id));
        const newActs=r.data.filter(a=>!localIds.has(a.id));
        if(newActs.length>0){S.acts=[...S.acts,...newActs];saveActs();renderNav();
          showToast(`📥 ${r.author} la til ${newActs.length} aktivitet(er)`);}
      }
      if(r.type==="budget"&&Array.isArray(r.data)){
        const localIds=new Set(S.budgetEntries.map(e=>e.id));
        const ne=r.data.filter(e=>!localIds.has(e.id));
        if(ne.length>0){S.budgetEntries=[...S.budgetEntries,...ne];saveBudget();
          showToast(`📥 ${r.author} la til ${ne.length} utgift(er)`);}
      }
      if(r.type==="pack"&&r.data&&typeof r.data==="object"){
        Object.assign(S.packChk,r.data);savePackChk();
        showToast(`📥 ${r.author} oppdaterte pakkelisten`);
      }
      S.lastSync=new Date();
      if(S.tab==="mine"||S.tab==="budsjett"||S.tab==="pakk")renderPage();
    })
    .subscribe();
}

export function renderSyncBanner(){
  if(!SB_READY)return"";
  if(S.syncEnabled){
    return`<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:10px 14px;margin-bottom:12px;background:rgba(52,211,153,.08);border:1px solid rgba(52,211,153,.2);border-radius:var(--r2)">
      <div>
        <div style="font-size:12px;font-weight:600;color:var(--green)">👨‍👩‍👧‍👦 Delt som ${esc(S.familyName)}</div>
        <div style="font-size:10px;color:var(--t3);margin-top:2px" id="sync-status">Synker...</div>
      </div>
      <div style="display:flex;gap:6px">
        <button onclick="syncAllToCloud().then(()=>loadSharedState())" style="padding:5px 10px;background:var(--bg3);border:1px solid var(--border);border-radius:var(--r3);font-size:11px;font-weight:600;cursor:pointer;color:var(--t1);font-family:var(--font)">🔄</button>
        <button onclick="disableFamilySync()" style="padding:5px 10px;background:rgba(248,113,113,.1);border:1px solid rgba(248,113,113,.2);border-radius:var(--r3);font-size:11px;font-weight:600;cursor:pointer;color:var(--red);font-family:var(--font)">Stopp</button>
      </div>
    </div>`;
  }
  return`<div style="display:flex;align-items:center;gap:10px;padding:12px 14px;margin-bottom:12px;background:rgba(167,139,250,.08);border:1px solid rgba(167,139,250,.2);border-radius:var(--r2)">
    <div style="flex:1">
      <div style="font-size:12px;font-weight:600;color:var(--purple)">👨‍👩‍👧‍👦 Del med familien</div>
      <div style="font-size:10.5px;color:var(--t3);margin-top:2px">Synk aktiviteter, budsjett og pakkeliste</div>
    </div>
    <div style="display:flex;gap:6px;align-items:center">
      <input id="family-name-input" placeholder="Ditt navn" value="${esc(S.familyName||S.diaryAuthor)}" style="width:90px;padding:5px 8px;background:var(--bg2);border:1px solid var(--border);border-radius:var(--r3);font-size:11px;color:var(--text);font-family:var(--font)">
      <button onclick="enableFamilySync()" style="padding:5px 10px;background:var(--purple);color:#fff;border:none;border-radius:var(--r3);font-size:11px;font-weight:700;cursor:pointer;font-family:var(--font)">Start</button>
    </div>
  </div>`;
}
